import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@mui/material';
import { useWindowVirtualizer, VirtualItem } from '@tanstack/react-virtual';

import { useClientContext } from 'context/ClientContext';
import { usePedidoCompraContext } from 'context/PedidoCompraContext';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { useListMode } from 'hooks/useListMode';
import { PedidoCompraGridProduct, PedidoCompraListProduct } from 'pages/compra/pedido/components';
import { ProdutoPedido } from 'pages/compra/pedido/types';

type GridRow = {
    virtualRow: VirtualItem;
    produtosRequest: ProdutoPedido[];
    numColumns: number;
    scrollMargin: number;
};

type ListRow = {
    virtualRow: VirtualItem;
    produtosRequest: ProdutoPedido[];
    scrollMargin: number;
};

/**
 * Componente responsável por renderizar uma linha individual da lista de produtos no modo grade
 */
const GridRow = ({ virtualRow, produtosRequest, numColumns, scrollMargin }: GridRow) => {
    const rowIndex = virtualRow.index;

    return (
        <div
            style={{
                display: 'flex',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start - scrollMargin}px)`,
            }}
        >
            {Array.from({ length: numColumns }).map((_, columnIndex) => {
                const prodIndex = rowIndex * numColumns + columnIndex;
                const produtoPedido = produtosRequest.at(prodIndex);

                if (!produtoPedido) return null;

                const gap = 16;
                const padLeft = (columnIndex * gap) / numColumns;
                const padRight = ((numColumns - 1 - columnIndex) * gap) / numColumns;

                return (
                    <div
                        key={produtoPedido.produto.idProduto}
                        style={{
                            width: `${100 / numColumns}%`,
                            height: '100%',
                            paddingLeft: padLeft,
                            paddingRight: padRight,
                            paddingBottom: 16,
                        }}
                    >
                        <PedidoCompraGridProduct produtoPedido={produtoPedido} />
                    </div>
                );
            })}
        </div>
    );
};

/**
 * Componente responsável por renderizar uma linha individual da lista de produtos
 */
const ListRow = ({ virtualRow, produtosRequest, scrollMargin }: ListRow) => {
    const produtoPedido = produtosRequest.at(virtualRow.index);

    if (!produtoPedido) return null;

    return (
        <div
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start - scrollMargin}px)`,
                paddingBottom: 8,
                boxSizing: 'border-box',
            }}
        >
            <PedidoCompraListProduct produtoPedido={produtoPedido} />
        </div>
    );
};

export function PedidoCompraListContainer() {
    const { produtosRequest } = usePedidoCompraContext();
    const { isUpMd, isUpSm } = useBreakpoints();
    const { mode } = useListMode();
    const { tipoPedido } = useClientContext();
    const isTipoPedidoFuncionario = tipoPedido === 'funcionario';

    const isGridMode = mode === 'grid' || !isUpMd;
    const numColumns = isUpMd ? 3 : isUpSm ? 2 : 1;
    const numRows = useMemo((): number => Math.ceil(produtosRequest.length / numColumns), [numColumns, produtosRequest.length]);

    const alturaCardList = 100;
    const alturaCardGrid = isTipoPedidoFuncionario ? 416 : 476;

    const parentRef = useRef<HTMLDivElement>(null);
    const [scrollMargin, setScrollMargin] = useState(0);

    useLayoutEffect(() => {
        if (parentRef.current) {
            const rect = parentRef.current.getBoundingClientRect();
            const top = rect.top + window.scrollY;
            setScrollMargin(top);
        }
    }, [produtosRequest.length, isGridMode, numColumns]);

    const count = isGridMode ? numRows : produtosRequest.length;

    const estimateSize = useMemo(() => {
        return () => (isGridMode ? alturaCardGrid : alturaCardList);
    }, [isGridMode, alturaCardGrid, alturaCardList]);

    const rowVirtualizer = useWindowVirtualizer({
        count,
        estimateSize,
        overscan: 5,
        scrollMargin,
    });

    if (produtosRequest.length === 0) return null;

    return (
        <Box ref={parentRef} sx={{ width: '100%', position: 'relative', height: `${rowVirtualizer.getTotalSize()}px` }}>
            {rowVirtualizer
                .getVirtualItems()
                .map((virtualRow) =>
                    isGridMode ? (
                        <GridRow
                            key={virtualRow.key}
                            virtualRow={virtualRow}
                            produtosRequest={produtosRequest}
                            numColumns={numColumns}
                            scrollMargin={rowVirtualizer.options.scrollMargin}
                        />
                    ) : (
                        <ListRow
                            key={virtualRow.key}
                            virtualRow={virtualRow}
                            produtosRequest={produtosRequest}
                            scrollMargin={rowVirtualizer.options.scrollMargin}
                        />
                    ),
                )}
        </Box>
    );
}
